import React, { FC, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectActiveProperty } from '@hkm/components/Menu/PropertySelector/domain/selectors';
import DatetimeFormElement, {
  DatetimeVariant
} from '@hkm/components/shared/DatetimeFormElement/DatetimeFormElement';
import GuestServiceModal from '@hkm/components/shared/GuestService/GuestServiceModal';
import { GuestServiceStatusType } from '@hkm/shared/enum/guestServiceStatusType';
import { useEnabledGuestServiceStatuses } from '@hkm/shared/hooks/useEnabledGuestServiceStatuses';
import { useGuestServiceMap } from '@hkm/shared/hooks/useGuestServiceMap';
import { createLocalTimeFromTzTime } from '@hkm/utils/dateHelper';

import { AcFormElement } from '@ac/mobile-components/dist/components/form-element';
import { AcSelectField } from '@ac/mobile-components/dist/components/select';
import { Color } from '@ac/mobile-components/dist/enums';
import { Testable } from '@ac/mobile-components/dist/interfaces/componentProps';
import { formatTestSelector } from '@ac/mobile-components/dist/utils';

export interface GuestServiceFieldProps extends Testable {
  disabled?: boolean;
  isReadonly?: boolean;
  serviceDeferredUntil?: string;
  selectedGuestService?: GuestServiceStatusType;
  onChange(guestServiceStatus?: GuestServiceStatusType, endTime?: string): void;
}

/* tslint:disable:jsx-no-lambda */
const GuestServiceField: FC<GuestServiceFieldProps> = ({
  disabled,
  isReadonly,
  testSelector,
  selectedGuestService,
  onChange,
  serviceDeferredUntil
}) => {
  const { t } = useTranslation();
  const guestServiceMap = useGuestServiceMap();
  const enabledGuestServiceStatuses = useEnabledGuestServiceStatuses();
  const [
    guestServiceModalVisibility,
    setGuestServiceModalVisibility
  ] = useState<boolean>(false);

  const showGuestServiceModal = () => setGuestServiceModalVisibility(true);
  const hideGuestServiceModal = () => setGuestServiceModalVisibility(false);
  const clearedSelectedValue = () => onChange();

  const currentServiceSelected = selectedGuestService
    ? enabledGuestServiceStatuses.find(v => v.code === selectedGuestService)
    : undefined;

  const property = useSelector(selectActiveProperty);
  const deferredTime = createLocalTimeFromTzTime(
    serviceDeferredUntil,
    property.timeZoneCode
  );

  const defferedTimeComponent = useMemo(
    () =>
      deferredTime &&
      guestServiceMap.get(GuestServiceStatusType.ServiceDeferred) ? (
        <DatetimeFormElement
          color={Color.ServiceDeferred}
          datetime={deferredTime}
          variant={DatetimeVariant.onlyTime}
          timeLabel={t('GLOBAL.GUEST_SERVICE_STATUS.PREFERRED_TIME')}
        />
      ) : (
        <></>
      ),
    [deferredTime]
  );

  return (
    <>
      {enabledGuestServiceStatuses.length > 0 &&
        (!isReadonly || currentServiceSelected) &&
        (isReadonly ? (
          <>
            <AcFormElement
              label={t('GLOBAL.GUEST_SERVICE_STATUS.TITLE')}
              testSelector={formatTestSelector(
                testSelector,
                'guestServiceStatus'
              )}
            >
              {currentServiceSelected?.name}
            </AcFormElement>
            {defferedTimeComponent}
          </>
        ) : (
          <>
            <AcSelectField
              disabled={disabled}
              clearable={true}
              clearSelectedValue={clearedSelectedValue}
              onClick={showGuestServiceModal}
              placeholder={t('GLOBAL.SELECT')}
              value={currentServiceSelected?.name || ''}
              label={t('GLOBAL.GUEST_SERVICE_STATUS.TITLE')}
              testSelector={formatTestSelector(
                testSelector,
                'guestServiceStatus'
              )}
            />
            {defferedTimeComponent}
            <GuestServiceModal
              onConfirm={onChange}
              onClose={hideGuestServiceModal}
              open={guestServiceModalVisibility}
              testSelector={`${testSelector}-guest-service`}
              selectedGuestService={selectedGuestService}
              serviceDeferredUntil={serviceDeferredUntil}
            />
          </>
        ))}
    </>
  );
};

export default memo(GuestServiceField);



// WEBPACK FOOTER //
// ./src/components/shared/GuestService/GuestServiceField.tsx